import ApexCharts from 'apexcharts'
import Lang from '@/components/Lang'

export default class LeagueChart {
    #element
    #chart
    #players
    #idealPouleSize
    #levels

    constructor(element) {
        this.#element = element

        this.#players = document.getElementById(element.dataset.players)
        this.#idealPouleSize = document.getElementById(element.dataset.idealPouleSize)

        this.#levels = JSON.parse(element.dataset.levels).map((level) => ({
            name: level.name,
            promotes: level.hasOwnProperty('promotes')
                ? document.getElementById(level.promotes)
                : null,
            relegates: level.hasOwnProperty('relegates')
                ? document.getElementById(level.relegates)
                : null,
        }))

        this.#chart = new ApexCharts(element, {
            chart: {
                type: 'line',
                height: 320,
                zoom: {
                    enabled: false,
                },
                toolbar: {
                    tools: {
                        download: '<i class="bi bi-three-dots"></i>',
                    },
                },
            },
            series: [],
            xaxis: {
                title: {
                    text: Lang.get('campaign.leagues.week'),
                },
                tooltip: {
                    enabled: false,
                },
            },
            yaxis: {
                min: 0,
                decimalsInFloat: 0,
                title: {
                    text: Lang.get('campaign.leagues.number_of_poules'),
                },
            },
            tooltip: {
                x: {
                    formatter: function (value) {
                        return Lang.get('campaign.leagues.week_number', { number: value })
                    },
                },
                y: {
                    formatter: function (value) {
                        return Lang.get('campaign.leagues.poules', { count: value })
                    },
                },
            },
        })

        this.#chart.render()

        this.#bindEvents()
        this.#recalculate()
    }

    static initAll(selector) {
        Array.from(document.querySelectorAll(selector), (element) => new LeagueChart(element))
    }

    #bindEvents() {
        this.#players.addEventListener('change', this.#recalculate)
        this.#idealPouleSize.addEventListener('change', this.#recalculate)

        this.#levels.forEach((level) => {
            level.promotes?.addEventListener('change', this.#recalculate)
            level.relegates?.addEventListener('change', this.#recalculate)
        })
    }

    #recalculate = () => {
        let players = parseInt(this.#players.value)
        let idealPouleSize = parseInt(this.#idealPouleSize.value)

        let levels = this.#levels.map((level) => {
            return {
                name: level.name,
                promotes: parseInt(level.promotes?.value ?? 0),
                relegates: parseInt(level.relegates?.value ?? 0),
            }
        })

        let weeks = [
            levels.map((level, levelIndex) =>
                this.#level(
                    levelIndex === 0 ? players : 0,
                    idealPouleSize,
                    level.promotes,
                    level.relegates
                )
            ),
        ]

        for (let week = 0; week <= 20; week++) {
            let lastWeek = weeks[week]

            weeks.push(
                levels.map((level, levelIndex) => {
                    let below = lastWeek[levelIndex - 1]
                    let current = lastWeek[levelIndex]
                    let above = lastWeek[levelIndex + 1]

                    return this.#level(
                        current.players -
                            current.promotes -
                            current.relegates +
                            (below?.promotes ?? 0) +
                            (above?.relegates ?? 0),
                        idealPouleSize,
                        level.promotes,
                        level.relegates
                    )
                })
            )
        }

        let series = levels.map((level, levelIndex) => ({
            name: level.name,
            data: weeks.map((week) => week[levelIndex].poules),
        }))

        this.#chart.updateSeries(series)
    }

    #numberOfPoules = (players, idealSize) => {
        if (players === 0) {
            return 0
        }

        if (players < idealSize) {
            return 1
        }

        let idealPoules = players / idealSize

        if (idealPoules % 1 === 0) {
            return idealPoules
        }

        let distancesToIdeal_0 = Math.abs(idealSize - players / Math.floor(idealPoules))
        let distancesToIdeal_1 = Math.abs(idealSize - players / Math.ceil(idealPoules))

        if (distancesToIdeal_1 < distancesToIdeal_0) {
            return Math.ceil(idealPoules)
        }

        return Math.floor(idealPoules)
    }

    #level = (players, idealPouleSize, promotes, relegates) => {
        let poules = this.#numberOfPoules(players, idealPouleSize)

        return {
            players: players,
            poules: poules,
            promotes: poules * promotes,
            relegates: poules * relegates,
        }
    }
}
